const SANDBOX_FILE_PREFIX = "sandbox:/mnt/data/"
const SANDBOX_SCHEME = "sandbox:"
const ENCODED_SEPARATOR = /%(?:2f|5c)/i
const SANDBOX_CLASSIFICATION_DECODE_ROUNDS = 3
const ASCII_PERCENT_ESCAPE = /%([0-9a-f]{2})/gi
const NESTED_ENCODED_SANDBOX_PREFIX =
  /^(?:\s|%(?:25)*(?:09|0a|0b|0c|0d|20))*(?:s|%(?:25)*73)(?:a|%(?:25)*61)(?:n|%(?:25)*6e)(?:d|%(?:25)*64)(?:b|%(?:25)*62)(?:o|%(?:25)*6f)(?:x|%(?:25)*78)/i
const NESTED_ENCODED_COLON_PREFIX = /^%(?:25)*3a/i
const NESTED_ENCODED_PATH_SEPARATOR = String.raw`(?:[/\\]|%(?:25)*(?:2f|5c))`
const NESTED_ENCODED_MNT_DATA_PATH = new RegExp(
  `${NESTED_ENCODED_PATH_SEPARATOR}mnt${NESTED_ENCODED_PATH_SEPARATOR}data(?:${NESTED_ENCODED_PATH_SEPARATOR}|$)`,
  "i"
)
const CORRUPTED_SANDBOX_DELIMITER = /^(?:%?zz)|%(?![0-9a-f]{2})/i
const RAW_CONTROL_OR_RESERVED_DELIMITER = new RegExp(
  String.raw`[\u0000-\u002c\u002f\u003a-\u0040\u005b-\u005e\u0060\u007b-\u007d\u007f]`
)
const NESTED_ENCODED_CONTROL_OR_PUNCTUATION =
  /%(?:25)*(?:(?:0|1)[0-9a-f]|2[0-9a-f]|3[a-f]|40|5[b-f]|60|7[b-f])/i

export type ResolvedSandboxFileLink = {
  filename: string
  fileUrl: string
}

function hasSandboxPathProvenance(candidate: string): boolean {
  const prefix = NESTED_ENCODED_SANDBOX_PREFIX.exec(candidate)
  if (!prefix) return false

  const suffix = candidate.slice(prefix[0].length).toLowerCase()
  if (suffix.startsWith(":") || NESTED_ENCODED_COLON_PREFIX.test(suffix)) return true

  const mount = NESTED_ENCODED_MNT_DATA_PATH.exec(suffix)
  if (!mount) return false

  // A canonical mount immediately after `sandbox`, or after a malformed or
  // recursively encoded control/punctuation delimiter, is sandbox-owned.
  // Keep ordinary raw relative names such as `sandbox-guide/...` on the normal
  // link path even when their slash or backslash separators are deeply encoded.
  const delimiter = suffix.slice(0, mount.index)
  return (
    delimiter.length === 0 ||
    CORRUPTED_SANDBOX_DELIMITER.test(delimiter) ||
    RAW_CONTROL_OR_RESERVED_DELIMITER.test(delimiter) ||
    NESTED_ENCODED_CONTROL_OR_PUNCTUATION.test(delimiter)
  )
}

/** Identify every sandbox-shaped href so none can fall through to a raw anchor. */
export function isSandboxHref(href: string | undefined): boolean {
  if (!href) return false

  let candidate = href
  let sandboxProvenanceSeen = false
  for (let round = 0; round <= SANDBOX_CLASSIFICATION_DECODE_ROUNDS; round += 1) {
    candidate = candidate.trimStart()
    const lowerCandidate = candidate.toLowerCase()
    sandboxProvenanceSeen ||= hasSandboxPathProvenance(lowerCandidate)
    if (lowerCandidate.slice(0, SANDBOX_SCHEME.length) === SANDBOX_SCHEME) return true
    // Malformed model output can omit/corrupt the scheme delimiter while
    // retaining the unmistakable sandbox mount path. Treat that shape as
    // sandbox-owned too, so it cannot fall through as a clickable relative URL.
    if (hasSandboxPathProvenance(lowerCandidate)) return true
    const decoded = candidate.replace(ASCII_PERCENT_ESCAPE, (escape, hex: string) => {
      const value = Number.parseInt(hex, 16)
      return value <= 0x7f ? String.fromCharCode(value) : escape
    })
    if (decoded === candidate) return sandboxProvenanceSeen
    candidate = decoded
  }
  // A deeply nested percent-encoded value that still changes at the work
  // bound is noninteractive only when its prefix proves sandbox provenance.
  // Unrelated HTTPS/data paths must retain their normal renderer behavior.
  const finalCandidate = candidate.trimStart().toLowerCase()
  return (
    sandboxProvenanceSeen ||
    finalCandidate.startsWith(SANDBOX_SCHEME) ||
    hasSandboxPathProvenance(finalCandidate)
  )
}

export function isSandboxFileHref(href: string | undefined): boolean {
  if (!href || href.slice(0, SANDBOX_SCHEME.length).toLowerCase() !== SANDBOX_SCHEME) return false
  return href.slice(SANDBOX_SCHEME.length).startsWith("/mnt/data/")
}

/**
 * Resolve a model-emitted sandbox path only when it names an assistant file
 * already registered on the current session. No raw sandbox URL is returned.
 */
export function resolveSandboxFileLink(
  href: string | undefined,
  sessionId: string,
  assistantFiles: string[]
): ResolvedSandboxFileLink | null {
  if (!href || !isSandboxFileHref(href) || !sessionId) return null

  const encodedFilename = href.slice(SANDBOX_FILE_PREFIX.length)
  if (!encodedFilename || ENCODED_SEPARATOR.test(encodedFilename)) return null

  let filename: string
  try {
    filename = decodeURIComponent(encodedFilename)
  } catch {
    return null
  }

  if (
    !filename ||
    filename !== filename.trim() ||
    filename === "." ||
    filename === ".." ||
    filename.includes("/") ||
    filename.includes("\\") ||
    ENCODED_SEPARATOR.test(filename) ||
    Array.from(filename).some((character) => {
      const codePoint = character.codePointAt(0) ?? 0
      return codePoint < 32 || codePoint === 127
    }) ||
    !assistantFiles.includes(filename)
  ) {
    return null
  }

  return {
    filename,
    fileUrl: `/api/session-content/${encodeURIComponent(sessionId)}/${encodeURIComponent(filename)}`,
  }
}
